import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const EMAIL_RX = /^[^@\s,;<>]+@[^@\s,;<>]+\.[a-z]{2,}$/i;

/** Editable To list: chips, the partner's users as suggestions, or any typed address. */
export function RecipientPicker({ value, onChange, options }) {
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [focus, setFocus] = useState(false);
  const have = new Set(value.map((r) => r.email.toLowerCase()));
  const s = q.trim().toLowerCase();
  const left = options.filter((u) => !have.has(u.email.toLowerCase()));
  const matches = left.filter((u) => !s || `${u.name} ${u.email}`.toLowerCase().includes(s)).slice(0, 8);
  const known = (email) => options.find((u) => u.email.toLowerCase() === email) || { email, name: "" };
  const put = (rows) => { onChange([...value, ...rows]); setQ(""); setErr(""); };

  const addTyped = () => {
    const parts = q.split(/[\s,;]+/).filter(Boolean).map((p) => p.toLowerCase());
    if (!parts.length) return;
    const bad = parts.filter((p) => !EMAIL_RX.test(p));
    if (bad.length && matches.length && parts.length === 1) { put([matches[0]]); return; }
    if (bad.length) { setErr(`Not a valid e-mail address: ${bad.join(", ")}`); return; }
    put([...new Set(parts)].filter((p) => !have.has(p)).map(known));
  };

  return (
    <div data-testid="pw-recipients">
      <div className="flex flex-wrap gap-1.5" data-testid="pw-recipient-list">
        {!value.length && <span className="text-xs italic text-rose-600">No recipients yet — add at least one.</span>}
        {value.map((r) => (
          <span key={r.email} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700"
                data-testid={`pw-recipient-${r.email}`}>
            {r.name && <span className="font-medium">{r.name}</span>}<span className="text-slate-500">{r.email}</span>
            {r.role === "partner_admin" && <span className="rounded bg-slate-100 px-1 text-[9px] font-semibold uppercase text-slate-500">admin</span>}
            <button type="button" onClick={() => onChange(value.filter((x) => x.email !== r.email))} aria-label={`Remove ${r.email}`}
                    className="text-slate-400 hover:text-rose-600" data-testid={`pw-recipient-remove-${r.email}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="relative mt-2">
        <Input value={q} onChange={(e) => { setQ(e.target.value); setErr(""); }} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
               onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTyped(); } }}
               placeholder="Add a partner user, or type any e-mail and press Enter" className="h-9 text-sm" data-testid="pw-recipient-input" />
        {focus && s && matches.length > 0 && (
          <div className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg" data-testid="pw-recipient-matches">
            {matches.map((u) => (
              <button key={u.email} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => put([u])} data-testid={`pw-recipient-add-${u.email}`}
                      className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                <span>{u.name || u.email}{u.admin && <span className="ml-1.5 text-[10px] uppercase text-slate-400">admin</span>}</span>
                <span className="text-xs text-slate-400">{u.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {err && <p className="mt-1 text-xs text-rose-600" data-testid="pw-recipient-error">{err}</p>}
      {left.length > 0 && (
        <button type="button" onClick={() => put(left)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
                data-testid="pw-recipient-add-all"><Plus className="h-3 w-3" /> Add all {left.length} other partner user{left.length === 1 ? "" : "s"}</button>
      )}
    </div>
  );
}
